import { Component, computed, OnInit, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
interface Trainer {
  id: number;
  name: string;
  batch: "all" | "morning" | "evening";
  active: boolean;
}

// Interface for the calculated commission result
interface CommissionResult extends Trainer {
  commission: number;
  percentage: number;
}
@Component({
  selector: "app-trainer-contract",
  imports: [CommonModule],
  templateUrl: "./trainer-contract.component.html",
  styleUrl: "./trainer-contract.component.scss",
})
export class TrainerContractComponent implements OnInit {
  ngOnInit(): void {
    this.calculateCommissions();
  }

  //#region variables
  centers: any[] = [
    {
      name: "Main Center (Single Coach Scenario)",
      commissionModel: "center-wide",
      coaches: [{ name: "Harsh", commissionText: "" }],
    },
    {
      name: "Westside Gym (Multiple Coaches, Shared Commission)",
      commissionModel: "center-wide",
      coaches: [
        { name: "Vishal", commissionText: "" },
        { name: "Parth", commissionText: "" },
      ],
    },
    {
      name: "Uptown Fitness (Batch-wise Commission)",
      commissionModel: "batch-wise",
      coaches: [
        { name: "Vishal", batch: "Morning", commissionText: "" },
        { name: "Parth", batch: "Evening", commissionText: "" },
      ],
    },
  ];
  centerName = signal("Main Fitness Center");
  totalRevenue = signal(50000);
  commissionRate = signal(30);
  distributionType = signal<"equal" | "batch">("equal");
  trainers = signal<Trainer[]>([{ id: 1, name: "Harsh", batch: "all", active: true }]);
  activeTrainers = computed(() => this.trainers().filter(t => t.active));
  totalCommissionAmount = computed(() => (this.totalRevenue() * this.commissionRate()) / 100);
  // --- METHODS ---

  //#endregion

  //#region methods
  calculateCommissions(): void {
    this.centers.forEach(center => {
      // Scenario 1: Only one coach in the center
      if (center.coaches.length === 1) {
        center.coaches[0].commissionText = "Receives 100% of the total center commission.";
      }
      // Scenario 2: Multiple coaches sharing commission for the whole center
      else if (center.coaches.length > 1 && center.commissionModel === "center-wide") {
        const percentage = (100 / center.coaches.length).toFixed(2);
        center.coaches.forEach((coach: any) => {
          coach.commissionText = `Receives an equal share (${percentage}%) of the total center commission.`;
        });
      }
      // Scenario 3: Commission is split by batches (Morning/Evening)
      else if (center.commissionModel === "batch-wise") {
        center.coaches.forEach((coach: any) => {
          coach.commissionText = `Receives commission based on the revenue generated during their assigned ${coach.batch} batch.`;
        });
      }
    });
  }

  commissionResults = computed<CommissionResult[]>(() => {
    const totalCommission = this.totalCommissionAmount();
    const active = this.activeTrainers();
    const distType = this.distributionType();

    if (active.length === 0) return [];

    if (active.length === 1) {
      return active.map(t => ({ ...t, commission: totalCommission, percentage: 100 }));
    }

    if (distType === "equal") {
      const perTrainer = totalCommission / active.length;
      return active.map(t => ({
        ...t,
        commission: perTrainer,
        percentage: 100 / active.length,
      }));
    }

    if (distType === "batch") {
      const morningTrainers = active.filter(t => t.batch === "morning");
      const eveningTrainers = active.filter(t => t.batch === "evening");
      const allDayTrainers = active.filter(t => t.batch === "all");

      // Revenue distribution: 40% Morning, 40% Evening, 20% All Day
      const morningRevenue = this.totalRevenue() * 0.4;
      const eveningRevenue = this.totalRevenue() * 0.4;
      const allDayRevenue = this.totalRevenue() * 0.2;

      const results: CommissionResult[] = [];

      const calculateBatch = (batchTrainers: Trainer[], batchRevenue: number) => {
        if (batchTrainers.length > 0) {
          const batchComm = (batchRevenue * this.commissionRate()) / 100;
          const perTrainer = batchComm / batchTrainers.length;
          batchTrainers.forEach(t => {
            results.push({
              ...t,
              commission: perTrainer,
              percentage: (perTrainer / totalCommission) * 100,
            });
          });
        }
      };

      calculateBatch(morningTrainers, morningRevenue);
      calculateBatch(eveningTrainers, eveningRevenue);
      calculateBatch(allDayTrainers, allDayRevenue);

      return results;
    }

    return [];
  });

  // --- METHODS ---
  addTrainer(): void {
    // Find the highest existing ID to safely create a new unique ID
    const newId = this.trainers().length > 0 ? Math.max(...this.trainers().map(t => t.id)) + 1 : 1;
    this.trainers.update(currentTrainers => [...currentTrainers, { id: newId, name: "", batch: "all", active: true }]);
  }

  removeTrainer(idToRemove: number): void {
    this.trainers.update(currentTrainers => currentTrainers.filter(t => t.id !== idToRemove));
  }

  updateTrainer(idToUpdate: number, field: keyof Omit<Trainer, "id">, value: any): void {
    this.trainers.update(currentTrainers => currentTrainers.map(t => (t.id === idToUpdate ? { ...t, [field]: value } : t)));
  }
}
